import { useLayoutEffect, useState, type RefObject } from "react";

type CursorPosition = { x: number; y: number; ready: boolean };

export function useTargetCursor(
  rootRef: RefObject<HTMLElement | null>,
  target: string | null
) {
  const [position, setPosition] = useState<CursorPosition>({
    x: 0,
    y: 0,
    ready: false
  });

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    let frame = 0;

    const update = () => {
      frame = 0;
      if (!target) {
        setPosition((current) => ({ ...current, ready: false }));
        return;
      }
      const targetNode = root.querySelector<HTMLElement>(
        `[data-cursor-target="${target}"]`
      );
      if (!targetNode) return;
      const rootRect = root.getBoundingClientRect();
      const targetRect = targetNode.getBoundingClientRect();
      setPosition({
        x: targetRect.left - rootRect.left + targetRect.width * 0.56,
        y: targetRect.top - rootRect.top + targetRect.height * 0.54,
        ready: true
      });
    };

    const requestUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    requestUpdate();
    const delayed = window.setTimeout(requestUpdate, 90);
    const resizeObserver = new ResizeObserver(requestUpdate);
    resizeObserver.observe(root);
    window.addEventListener("resize", requestUpdate);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.clearTimeout(delayed);
      resizeObserver.disconnect();
      window.removeEventListener("resize", requestUpdate);
    };
  }, [rootRef, target]);

  return position;
}
